/**
 * Which roles this person may put somebody into.
 *
 * A mirror of `public.may_assign_role()` in migration 0011, and a deliberate
 * second copy — with the same discipline the scoping rule gets: the database is
 * the authority, this exists only so a role the database will refuse is never
 * offered in a dropdown, and an integration test asserts the two agree for
 * every role of a real company rather than trusting that they do.
 *
 * The rule, both here and there:
 *
 *   an owner role may be assigned only by an owner;
 *   any other role may be assigned only by somebody who holds every capability
 *   in it.
 *
 * The second half is what stops `team.manage` being a way to mint power: you
 * can hand out what you have, and nothing more.
 */
import type { TenantContext } from '@timely/domain';

export interface AssignableRole {
    id: string;
    name: string;
    isOwner: boolean;
    capabilities: readonly string[];
}

export function mayAssignRole(context: TenantContext, role: AssignableRole): boolean {
    const { membership } = context;
    if (membership.status !== 'active') return false;

    if (role.isOwner) return membership.isOwner;

    // An owner holds everything, listed or not — the database's `has_cap()`
    // returns true for an owner without consulting the array, and so does this.
    if (membership.isOwner) return true;

    const held = new Set<string>(membership.capabilities);
    return role.capabilities.every(capability => held.has(capability));
}

export const assignableRoles = (context: TenantContext, roles: AssignableRole[]): AssignableRole[] =>
    roles.filter(role => mayAssignRole(context, role));

/**
 * Whether this member is the only active owner left.
 *
 * `guard_last_owner` refuses to let a company end up with nobody who can run
 * it, and there is no support path that can fix one from outside — by design,
 * because a support path into a customer's data is a door. So the screen says
 * why the button is off rather than letting somebody press it and read a
 * database error.
 */
export interface TeamMemberLike {
    memberId: string;
    isOwner: boolean;
    status: string;
}

export const isLastOwner = (members: TeamMemberLike[], memberId: string): boolean => {
    const owners = members.filter(m => m.isOwner && m.status === 'active');
    return owners.length === 1 && owners[0]!.memberId === memberId;
};

/**
 * Why a member's controls are disabled, or null if they are not.
 *
 * One function so the Team screen cannot disable a control without being able
 * to say why, which is the difference between a considered restriction and a
 * bug that looks like one.
 */
export function whyNotEditable(
    context: TenantContext,
    members: TeamMemberLike[],
    member: TeamMemberLike & { isSelf: boolean },
): string | null {
    if (member.isSelf) {
        return 'You cannot change your own role or status. Another owner can.';
    }
    // Order matters when more than one applies. A delegate looking at the only
    // owner is in both cases, and "only an owner can change an owner" is the
    // one they can do something about — the other asks them to promote
    // somebody, which is precisely what they may not do.
    if (member.isOwner && !context.membership.isOwner) {
        return 'Only an owner can change an owner.';
    }
    if (isLastOwner(members, member.memberId)) {
        return 'A company must always have an owner. Make somebody else an owner first.';
    }
    return null;
}
